import type { TerminalConnectionState } from '../terminal/use-pty-terminal';
import { statusLabel } from '../terminal/terminal-status';
import {
  PRO_BUILDER_TERMINAL_ENDED,
  PRO_BUILDER_TERMINAL_FAILED,
  PRO_BUILDER_TERMINAL_RECONNECTING,
} from './pro-builder-copy';

/**
 * What the Pro Builder terminal shows over itself (FRONT-3003), as one pure
 * decision so the visible message and the screen-reader status cannot drift.
 *
 * The overlay — with its Reconnect button, which re-opens and attaches when the
 * shell is somehow still alive and spawns a new one when it is not — shows only
 * when there is no session left to keep: the shell ended, or the hook gave up and said why (retries
 * used up, or the open itself failed). A stream that dropped and is being
 * retried is `error` with no message while `reconnecting`: the shell is still
 * alive server-side, so it gets a quiet "Reconnecting…" line and no button.
 */
export type ProBuilderTerminalView =
  | { kind: 'ended' }
  | { kind: 'failed'; message: string }
  | { kind: 'reconnecting' }
  | { kind: 'live' };

export function proBuilderTerminalView(
  state: TerminalConnectionState,
  errorMessage: string | null,
  reconnecting: boolean,
): ProBuilderTerminalView {
  if (state === 'ended') return { kind: 'ended' };
  if (state === 'error' && errorMessage) return { kind: 'failed', message: errorMessage };
  if (state === 'error' && reconnecting) return { kind: 'reconnecting' };
  return { kind: 'live' };
}

/** The screen-reader status: the very sentence shown, or the connection label when nothing is shown. */
export function proBuilderTerminalStatusText(view: ProBuilderTerminalView, state: TerminalConnectionState): string {
  switch (view.kind) {
    case 'ended':
      return PRO_BUILDER_TERMINAL_ENDED;
    case 'failed':
      return PRO_BUILDER_TERMINAL_FAILED;
    case 'reconnecting':
      return PRO_BUILDER_TERMINAL_RECONNECTING;
    case 'live':
      return statusLabel(state);
  }
}
